'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { roomPath, SITE_URL } from '@/lib/seo';
import { ArrowRight, Check, LogIn, Loader2, Share2 } from 'lucide-react';

type Props = { realmId: string; slug: string; type: string; signedIn: boolean; isMember: boolean };
export function RoomActions({ realmId, slug, type, signedIn, isMember }: Props) {
  const router = useRouter();
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inviteCode, setInviteCode] = useState('');
  const [onlineCount, setOnlineCount] = useState<number | null>(null);
  const [connectionState, setConnectionState] = useState<'connecting' | 'connected' | 'unavailable'>('connecting');
  const [shareState, setShareState] = useState<'idle' | 'copied' | 'manual'>('idle');
  const loginUrl = `/login?next=${encodeURIComponent(roomPath(slug))}`;

  useEffect(() => {
    if (type !== 'PUBLIC' && !isMember) return;
    const ws = new WebSocket(process.env.NEXT_PUBLIC_WS_URL || 'wss://speedmixr.com/ws');
    ws.onopen = () => { setConnectionState('connected'); ws.send(JSON.stringify({ type: 'subscribe_realm', realmId })); };
    ws.onmessage = event => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'realm_online_count' && data.realmId === realmId && Number.isInteger(data.count) && data.count >= 0) { setOnlineCount(data.count); setConnectionState('connected'); }
      } catch { /* Ignore unrelated messages. */ }
    };
    ws.onclose = () => { setOnlineCount(null); setConnectionState('unavailable'); };
    ws.onerror = () => { setOnlineCount(null); setConnectionState('unavailable'); };
    return () => ws.close();
  }, [realmId, type, isMember]);

  async function join(event: React.FormEvent) {
    event.preventDefault();
    setJoining(true); setError(null);
    try {
      const res = await fetch('/api/realms/join', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ realmId, inviteCode }),
      });
      if (res.status === 401) { router.push(loginUrl); return; }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not join this room');
      router.push(`/lobby?realm=${encodeURIComponent(realmId)}`);
    } catch (err) { setError(err instanceof Error ? err.message : 'Could not join this room'); }
    finally { setJoining(false); }
  }

  async function shareRoom() {
    try { await navigator.clipboard.writeText(`${SITE_URL}${roomPath(slug)}`); setShareState('copied'); }
    catch { setShareState('manual'); }
  }

  return <div className="room-actions">
    {(type === 'PUBLIC' || isMember) && <div className={`room-live-status ${onlineCount && onlineCount > 0 ? 'is-online' : ''}`} role="status">
      <span className="room-status-dot" aria-hidden="true" /><div><strong>{onlineCount !== null ? onlineCount > 0 ? `${onlineCount} online in this room` : 'No members online' : connectionState === 'unavailable' ? 'Live status unavailable' : 'Checking room activity…'}</strong><p>{onlineCount === 0 ? 'You can wait for another member in the lobby.' : 'Matching starts when two members are ready.'}</p></div>
    </div>}
    {!signedIn ? <Link href={loginUrl} className="room-primary-action"><LogIn size={18} aria-hidden="true" />Sign in to join<ArrowRight size={17} aria-hidden="true" /></Link>
      : isMember ? <Link href={`/lobby?realm=${encodeURIComponent(realmId)}`} className="room-primary-action">Enter room<ArrowRight size={18} aria-hidden="true" /></Link>
      : type === 'CURATED' ? <p className="room-approval-note">Contact the room organizer for membership approval.</p>
      : <form onSubmit={join}>
        {type === 'PRIVATE' && <label className="room-invite-label">Invitation code
          <input required value={inviteCode} onChange={e => setInviteCode(e.target.value)} autoComplete="off" className="room-invite-input" placeholder="Enter your invitation code" />
        </label>}
        {error && <p role="alert" className="room-action-error">{error}</p>}
        <button disabled={joining} className="room-primary-action">{joining ? <><Loader2 size={18} className="animate-spin" aria-hidden="true" />Joining…</> : <>Join room<ArrowRight size={18} aria-hidden="true" /></>}</button>
      </form>}
    {(!signedIn || type !== 'CURATED' || isMember) && <p className="room-action-hint">You choose when to start matching.</p>}
    {type === 'PUBLIC' && <><button type="button" className="room-share-action" onClick={shareRoom}>{shareState === 'copied' ? <Check size={16} aria-hidden="true" /> : <Share2 size={16} aria-hidden="true" />}{shareState === 'copied' ? 'Link copied' : 'Copy room link'}</button><span role="status" className="sr-only">{shareState === 'copied' ? 'Room link copied to clipboard' : ''}</span>{shareState === 'manual' && <label className="room-copy-fallback">Copy this link<input readOnly value={`${SITE_URL}${roomPath(slug)}`} onFocus={e => e.target.select()} /></label>}</>}
  </div>;
}
